import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";

// ── 라우트 코드 스플리팅(lazy) — 사용자/관리자 전 라우트 분리해 초기 번들 경량화 ──
const GoogleAuthCallback = lazy(() => import("./pages/GoogleAuthCallback"));
const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Shop = lazy(() => import("./pages/Shop"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Missions = lazy(() => import("./pages/Missions"));
const Receipts = lazy(() => import("./pages/Receipts"));
const Battle = lazy(() => import("./pages/Battle"));
const PlanDetail = lazy(() => import("./pages/PlanDetail"));
const Profile = lazy(() => import("./pages/Profile"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const Withdrawals = lazy(() => import("./pages/Withdrawals"));
const Terms = lazy(() => import("./pages/Terms"));
const Spin = lazy(() => import("./pages/Spin"));
const Steps = lazy(() => import("./pages/Steps"));
const StepsRanking = lazy(() => import("./pages/StepsRanking"));
const Attendance = lazy(() => import("./pages/Attendance"));
const Feed = lazy(() => import("./pages/Feed"));
const FeedDetail = lazy(() => import("./pages/FeedDetail"));
const Support = lazy(() => import("./pages/Support"));
const PublicProduct = lazy(() => import("./pages/PublicProduct"));

const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminHome = lazy(() => import("./pages/admin/AdminHome"));
const AdminReceipts = lazy(() => import("./pages/admin/AdminReceipts"));
const AdminWithdrawals = lazy(() => import("./pages/admin/AdminWithdrawals"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminBattles = lazy(() => import("./pages/admin/AdminBattles"));
const AdminPolicies = lazy(() => import("./pages/admin/AdminPolicies"));
const AdminPoliciesHistory = lazy(() => import("./pages/admin/AdminPoliciesHistory"));
const AdminAds = lazy(() => import("./pages/admin/AdminAds"));
const AdminGrades = lazy(() => import("./pages/admin/AdminGrades"));
const AdminStats = lazy(() => import("./pages/admin/AdminStats"));
const AdminTerms = lazy(() => import("./pages/admin/AdminTerms"));
const AdminErrors = lazy(() => import("./pages/admin/AdminErrors"));
const AdminOnboarding = lazy(() => import("./pages/admin/AdminOnboarding"));
const AdminDummyCleanup = lazy(() => import("./pages/admin/AdminDummyCleanup"));
import RouteStepsSync from "./components/RouteStepsSync";
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminPoints = lazy(() => import("./pages/admin/AdminPoints"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminCoupons = lazy(() => import("./pages/admin/AdminCoupons"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminAdmins = lazy(() => import("./pages/admin/AdminAdmins"));
const AdminSurveys = lazy(() => import("./pages/admin/AdminSurveys"));
const AdminBanners = lazy(() => import("./pages/admin/AdminBanners"));
const AdminSupport = lazy(() => import("./pages/admin/AdminSupport"));
const AdminAbuse = lazy(() => import("./pages/admin/AdminAbuse"));
const AdminSpin = lazy(() => import("./pages/admin/AdminSpin"));
const AdminReferrals = lazy(() => import("./pages/admin/AdminReferrals"));
const AdminPointEvents = lazy(() => import("./pages/admin/AdminPointEvents"));
const AdminFeed = lazy(() => import("./pages/admin/AdminFeed"));
const AdminPopups = lazy(() => import("./pages/admin/AdminPopups"));
const AdminPush = lazy(() => import("./pages/admin/AdminPush"));
const AdminSteps = lazy(() => import("./pages/admin/AdminSteps"));
const AdminDesign = lazy(() => import("./pages/admin/AdminDesign"));

import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import InAppBrowserGuard from "./components/InAppBrowserGuard";
import InstallPrompt from "./components/InstallPrompt";
import CelebrationToaster from "./components/CelebrationToaster";
import RouteFallback from "./components/RouteFallback";
import LazyBoundary from "./components/LazyBoundary";
import "./lib/toastPatch";
import { ThemeProvider } from "./components/ThemeProvider";
import { MenuConfigProvider } from "./context/MenuConfigContext";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { initNativePush } from "./lib/native/nativePush";
import { initRewardAds } from "./lib/native/rewardAd";
import { initSafeArea } from "./lib/native/safeArea";
import { fetchAndApplyActiveTheme, startThemeAutoRefresh } from "./lib/theme";

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/dashboard" replace /> : <div className="user-scope"><Login /></div>;
}

function AppRouter() {
  return (
    <>
    <RouteStepsSync />
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/auth/google" element={<GoogleAuthCallback />} />
      <Route path="/p/:productId" element={<PublicProduct />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/shop" element={<ProtectedRoute><Shop /></ProtectedRoute>} />
      <Route path="/shop/:id" element={<ProtectedRoute><ProductDetail /></ProtectedRoute>} />
      <Route path="/missions" element={<ProtectedRoute><Missions /></ProtectedRoute>} />
      <Route path="/receipts" element={<ProtectedRoute><Receipts /></ProtectedRoute>} />
      <Route path="/battle" element={<ProtectedRoute><Battle /></ProtectedRoute>} />
      <Route path="/battle/:id" element={<ProtectedRoute><PlanDetail /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
      <Route path="/withdrawals" element={<ProtectedRoute><Withdrawals /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
      <Route path="/spin" element={<ProtectedRoute><Spin /></ProtectedRoute>} />
      <Route path="/steps" element={<ProtectedRoute><Steps /></ProtectedRoute>} />
      <Route path="/steps/ranking" element={<ProtectedRoute><StepsRanking /></ProtectedRoute>} />
      <Route path="/attendance" element={<ProtectedRoute><Attendance /></ProtectedRoute>} />
      <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
      <Route path="/feed/:id" element={<ProtectedRoute><FeedDetail /></ProtectedRoute>} />
      <Route path="/checkout/success" element={<ProtectedRoute><CheckoutSuccess /></ProtectedRoute>} />
      <Route path="/terms/:type" element={<ProtectedRoute><Terms /></ProtectedRoute>} />

      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminHome />} />
        <Route path="receipts" element={<AdminReceipts />} />
        <Route path="withdrawals" element={<AdminWithdrawals />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="battles" element={<AdminBattles />} />
        <Route path="dummy-cleanup" element={<AdminDummyCleanup />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="points" element={<AdminPoints />} />
        <Route path="coupons" element={<AdminCoupons />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="admins" element={<AdminAdmins />} />
        <Route path="surveys" element={<AdminSurveys />} />
        <Route path="banners" element={<AdminBanners />} />
        <Route path="support" element={<AdminSupport />} />
        <Route path="abuse" element={<AdminAbuse />} />
        <Route path="spin" element={<AdminSpin />} />
        <Route path="referrals" element={<AdminReferrals />} />
        <Route path="point-events" element={<AdminPointEvents />} />
        <Route path="steps" element={<AdminSteps />} />
        <Route path="feed" element={<AdminFeed />} />
        <Route path="popups" element={<AdminPopups />} />
        <Route path="push" element={<AdminPush />} />
        <Route path="policies" element={<AdminPolicies />} />
        <Route path="design" element={<AdminDesign />} />
        <Route path="policies/history" element={<AdminPoliciesHistory />} />
        <Route path="ads" element={<AdminAds />} />
        <Route path="grades" element={<AdminGrades />} />
        <Route path="stats" element={<AdminStats />} />
        <Route path="terms" element={<AdminTerms />} />
        <Route path="errors" element={<AdminErrors />} />
        <Route path="onboarding" element={<AdminOnboarding />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  React.useEffect(() => {
    document.title = "제로마진 ZeroMargin · 쇼핑 리워드";
    // 네이티브(Capacitor) 전용 초기화 — 웹에서는 내부 가드로 no-op.
    initNativePush();
    initRewardAds();
    // 네이티브 정확한 safe-area 인셋 주입(edge-to-edge 헤더/하단바 가림 방지). 웹은 no-op.
    initSafeArea();
    // 관리자가 적용한 active 테마를 받아 앱 전역 색 적용(실패 시 기본값 유지).
    fetchAndApplyActiveTheme();
    // 진입/포그라운드 복귀 시 현재 테마 재확인(자동 전환 슬롯 경계 자연 반영).
    startThemeAutoRefresh();
  }, []);
  return (
    <div className="App bg-[#F0F3F5] dark:bg-[#13161c] min-h-screen">
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <MenuConfigProvider>
                <LazyBoundary>
                  <Suspense fallback={<RouteFallback />}>
                    <AppRouter />
                  </Suspense>
                </LazyBoundary>
                <InAppBrowserGuard />
                <InstallPrompt />
                <CelebrationToaster />
              </MenuConfigProvider>
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </div>
  );
}
